import { CopyOutlined } from "@ant-design/icons";
import { App } from "antd";
import React from "react";
import { Contract } from "tm-shared/types";

import { useCatalog } from "@/domains/product";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import {
    StyledAPContractTableAdditionalKey,
    StyledAPContractTableProductRow,
    StyledAPContractTableProductWrapper,
    StyledAPContractTableRow,
    StyledProductBarcode,
} from "../../../Styled";
import { getDayWord } from "../..";

interface ContractAdditionalInfoProps {
    contract: Contract;
}

export const ContractAdditionalInfo: React.FC<ContractAdditionalInfoProps> = ({
    contract,
}) => {
    const { notification } = App.useApp();

    const { products } = useCatalog();

    const renderBarcodeRow = (barcode: string) => {
        const name = products?.[barcode]?.name || "Невідомий товар";
        return (
            <StyledAPContractTableProductRow>
                <StyledAPContractTableAdditionalKey>
                    Штрихкод:
                </StyledAPContractTableAdditionalKey>
                <StyledProductBarcode
                    onClick={() =>
                        copyObjectToClipboard(
                            {
                                barcode,
                                name,
                            },
                            notification,
                        )
                    }
                >
                    {barcode} <CopyOutlined />
                </StyledProductBarcode>
            </StyledAPContractTableProductRow>
        );
    };

    switch (contract.type) {
        case "paidProductInput":
            return contract.context?.products?.length ? (
                <>
                    {contract.context.products.map((product) => (
                        <StyledAPContractTableRow key={product.barcode}>
                            <StyledAPContractTableProductWrapper>
                                <StyledAPContractTableProductRow>
                                    <SecondaryText>
                                        {product.name}
                                    </SecondaryText>
                                    <PrimaryText
                                        style={{
                                            textAlign: "right",
                                            textWrap: "nowrap",
                                        }}
                                    >
                                        {product.quantity}
                                    </PrimaryText>
                                </StyledAPContractTableProductRow>
                                {product.barcode &&
                                    renderBarcodeRow(product.barcode)}
                                <StyledAPContractTableProductRow>
                                    <StyledAPContractTableAdditionalKey>
                                        Вартість введення товару:
                                    </StyledAPContractTableAdditionalKey>
                                    <PrimaryText style={{ textAlign: "right" }}>
                                        {product.price}₴
                                    </PrimaryText>
                                </StyledAPContractTableProductRow>
                            </StyledAPContractTableProductWrapper>
                        </StyledAPContractTableRow>
                    ))}
                    <StyledAPContractTableRow>
                        <SecondaryText>Період окупності:</SecondaryText>
                        <PrimaryText fontSize={16}>
                            {contract.context?.paybackPeriod
                                ? getDayWord(contract.context.paybackPeriod)
                                : "-"}
                        </PrimaryText>
                    </StyledAPContractTableRow>
                </>
            ) : null;

        case "shelfPayment":
            return (
                <StyledAPContractTableRow>
                    <SecondaryText>Умова оплати:</SecondaryText>
                    <PrimaryText fontSize={16}>
                        {contract.context?.paymentTerms || "-"}
                    </PrimaryText>
                </StyledAPContractTableRow>
            );

        case "additionalSalesPlace":
            return (
                <StyledAPContractTableRow>
                    <SecondaryText>Вартість місця:</SecondaryText>
                    <PrimaryText fontSize={16}>
                        {contract.context?.placePrice || "-"}₴
                    </PrimaryText>
                </StyledAPContractTableRow>
            );

        case "marketingCatalog":
            return (
                <StyledAPContractTableRow>
                    <SecondaryText>Вартість матеріалів:</SecondaryText>
                    <PrimaryText fontSize={16}>
                        {contract.context?.materialsPrice || "-"}₴
                    </PrimaryText>
                </StyledAPContractTableRow>
            );

        case "deferredCredit":
            return (
                <>
                    <StyledAPContractTableRow>
                        <SecondaryText>Відстрочка доставки:</SecondaryText>
                        <PrimaryText fontSize={16}>
                            {contract.context?.deferredDelivery || "-"}
                        </PrimaryText>
                    </StyledAPContractTableRow>
                    <StyledAPContractTableRow>
                        <SecondaryText>Відстрочка по Формі 1:</SecondaryText>
                        <PrimaryText fontSize={16}>
                            {contract.context?.deferredTermForm1 || "-"}
                        </PrimaryText>
                    </StyledAPContractTableRow>
                    <StyledAPContractTableRow>
                        <SecondaryText>Відстрочка по Формі 2</SecondaryText>
                        <PrimaryText fontSize={16}>
                            {contract.context?.deferredTermForm2 || "-"}
                        </PrimaryText>
                    </StyledAPContractTableRow>
                </>
            );

        case "retroBonusFixed":
            return (
                <StyledAPContractTableRow>
                    <SecondaryText>Сума витрат:</SecondaryText>
                    <PrimaryText fontSize={16}>
                        {contract.context?.costInCash || "-"}
                    </PrimaryText>
                </StyledAPContractTableRow>
            );

        case "retroBonusPercentage":
            return (
                <>
                    <StyledAPContractTableRow>
                        <SecondaryText>% знижки:</SecondaryText>
                        <PrimaryText fontSize={16}>
                            {contract.context?.discountInPercentage || "-"}
                        </PrimaryText>
                    </StyledAPContractTableRow>
                    <StyledAPContractTableRow>
                        <SecondaryText>Групи товарів:</SecondaryText>
                        {contract.context?.isAllFinGroups ? (
                            <PrimaryText fontSize={16}>Усі групи</PrimaryText>
                        ) : (
                            contract.context?.finGroups?.map((group) => (
                                <PrimaryText fontSize={16} key={group}>
                                    {group}
                                </PrimaryText>
                            ))
                        )}
                    </StyledAPContractTableRow>
                </>
            );

        case "assortmentMatrix":
            return contract.context?.barcodes?.length
                ? contract.context.barcodes.map((barcode) => (
                      <StyledAPContractTableRow key={barcode}>
                          <StyledAPContractTableProductWrapper>
                              <StyledAPContractTableProductRow>
                                  <PrimaryText>
                                      {products?.[barcode]?.name ||
                                          "Невідомий товар"}
                                  </PrimaryText>
                              </StyledAPContractTableProductRow>
                              {renderBarcodeRow(barcode)}
                          </StyledAPContractTableProductWrapper>
                      </StyledAPContractTableRow>
                  ))
                : null;
        case "facings":
            return contract?.context?.facings?.map((facing) => (
                <StyledAPContractTableRow key={facing.finGroup}>
                    <StyledAPContractTableProductWrapper>
                        <StyledAPContractTableProductRow>
                            <SecondaryText>{facing.finGroup}</SecondaryText>
                        </StyledAPContractTableProductRow>

                        <StyledAPContractTableProductRow>
                            <StyledAPContractTableAdditionalKey>
                                Планова кількість:
                            </StyledAPContractTableAdditionalKey>
                            <PrimaryText style={{ textAlign: "right" }}>
                                {facing?.quantity} шт.
                            </PrimaryText>
                        </StyledAPContractTableProductRow>
                    </StyledAPContractTableProductWrapper>
                </StyledAPContractTableRow>
            ));
        case "discount":
            return contract?.context?.discounts?.map((discount) => (
                <StyledAPContractTableRow key={discount.barcode}>
                    <StyledAPContractTableProductWrapper>
                        <StyledAPContractTableProductRow>
                            <SecondaryText>
                                {products?.[discount?.barcode || ""]?.name ||
                                    "Невідомий товар"}
                            </SecondaryText>
                        </StyledAPContractTableProductRow>
                        {discount.barcode && renderBarcodeRow(discount.barcode)}
                        <StyledAPContractTableProductRow>
                            <StyledAPContractTableAdditionalKey>
                                Очікувана вартість товару:
                            </StyledAPContractTableAdditionalKey>
                            <PrimaryText style={{ textAlign: "right" }}>
                                {discount?.price}₴
                            </PrimaryText>
                        </StyledAPContractTableProductRow>
                    </StyledAPContractTableProductWrapper>
                </StyledAPContractTableRow>
            ));
        default:
            return null;
    }
};
